import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { 
  ArrowLeft, 
  ExternalLink, 
  FileText, 
  MapPin, 
  Clock, 
  BarChart3,
  AlertTriangle,
  CheckCircle,
  MessageSquare
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PolicyDetail = () => {
  const { id } = useParams();
  const { language } = useLanguage();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPolicy();
  }, [id]);

  const fetchPolicy = async () => {
    try {
      const response = await axios.get(`${API}/policies/${id}`);
      setPolicy(response.data);
    } catch (error) {
      toast.error(language === 'es' ? 'Error al cargar política' : 'Failed to load policy');
    } finally {
      setLoading(false);
    }
  };

  const getRiskLevel = (score) => {
    if (score >= 70) return { label: language === 'es' ? 'Alto Riesgo' : 'High Risk', color: 'text-accent', bg: 'bg-accent/10' };
    if (score >= 40) return { label: language === 'es' ? 'Riesgo Medio' : 'Medium Risk', color: 'text-warning', bg: 'bg-warning/10' };
    return { label: language === 'es' ? 'Bajo Riesgo' : 'Low Risk', color: 'text-success', bg: 'bg-success/10' };
  };

  const getJurisdictionLabel = (jur) => {
    const labels = {
      federal: language === 'es' ? 'Federal' : 'Federal',
      state: language === 'es' ? 'Estatal' : 'State',
      local: language === 'es' ? 'Local' : 'Local'
    };
    return labels[jur] || jur;
  };

  const getCategoryLabel = (cat) => {
    const labels = {
      environmental: language === 'es' ? 'Ambiental' : 'Environmental',
      voting: language === 'es' ? 'Votación' : 'Voting',
      housing: language === 'es' ? 'Vivienda' : 'Housing',
      immigration: language === 'es' ? 'Inmigración' : 'Immigration',
      reproductive: language === 'es' ? 'Reproductivo' : 'Reproductive'
    };
    return labels[cat] || cat;
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="policy-detail-loading">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-12 w-3/4" />
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            <Skeleton className="h-48" />
            <Skeleton className="h-32" />
          </div>
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (!policy) {
    return (
      <div className="text-center py-12" data-testid="policy-not-found">
        <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
        <h2 className="text-xl font-semibold mb-2">
          {language === 'es' ? 'Política no encontrada' : 'Policy not found'}
        </h2>
        <Button asChild>
          <Link to="/policies">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {language === 'es' ? 'Volver a Políticas' : 'Back to Policies'}
          </Link>
        </Button>
      </div>
    );
  }

  const risk = getRiskLevel(policy.risk_score);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="policy-detail">
      {/* Back Button */}
      <Button variant="ghost" asChild className="mb-4" data-testid="back-btn">
        <Link to="/policies">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {language === 'es' ? 'Volver a Políticas' : 'Back to Policies'}
        </Link>
      </Button>

      {/* Header */}
      <div>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Badge variant="outline">{getCategoryLabel(policy.category)}</Badge>
          <Badge variant="secondary">{getJurisdictionLabel(policy.jurisdiction)}</Badge>
          {policy.state && <Badge variant="secondary">{policy.state}</Badge>}
          <Badge className={policy.status === 'pending' ? 'bg-warning/20 text-warning-foreground' : 'bg-success/20 text-success-foreground'}>
            {policy.status === 'pending' 
              ? (language === 'es' ? 'Pendiente' : 'Pending')
              : (language === 'es' ? 'Activo' : 'Active')
            }
          </Badge>
        </div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{policy.title}</h1>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="md:col-span-2 space-y-6">
          {/* Plain Language Summary */}
          <Card data-testid="summary-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                {language === 'es' ? 'Resumen en Lenguaje Simple' : 'Plain Language Summary'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                {language === 'es' && policy.plain_language_summary_es 
                  ? policy.plain_language_summary_es 
                  : policy.plain_language_summary || policy.description}
              </p>
            </CardContent>
          </Card>

          {/* Full Description */}
          <Card data-testid="description-card">
            <CardHeader>
              <CardTitle>
                {language === 'es' ? 'Descripción Completa' : 'Full Description'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">{policy.description}</p>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            <Button asChild data-testid="generate-comment-btn">
              <Link to={`/comments?policy=${policy.id}`}>
                <MessageSquare className="mr-2 h-4 w-4" />
                {language === 'es' ? 'Generar Comentario Público' : 'Generate Public Comment'}
              </Link>
            </Button>
            {policy.source_url && (
              <Button variant="outline" asChild data-testid="source-link">
                <a href={policy.source_url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  {language === 'es' ? 'Ver Fuente Original' : 'View Original Source'}
                </a>
              </Button>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Risk Score Card */}
          <Card data-testid="risk-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                {language === 'es' ? 'Evaluación de Riesgo' : 'Risk Assessment'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`p-4 rounded-lg ${risk.bg} mb-4`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`font-semibold ${risk.color}`}>{risk.label}</span>
                  <span className="text-2xl font-bold">{policy.risk_score}</span>
                </div>
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${
                      policy.risk_score >= 70 ? 'bg-accent' : 
                      policy.risk_score >= 40 ? 'bg-warning' : 'bg-success'
                    }`}
                    style={{ width: `${policy.risk_score}%` }}
                  />
                </div>
              </div>
              
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  {policy.risk_score >= 70 ? (
                    <AlertTriangle className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                  ) : policy.risk_score >= 40 ? (
                    <AlertTriangle className="h-4 w-4 text-warning mt-0.5 flex-shrink-0" />
                  ) : (
                    <CheckCircle className="h-4 w-4 text-success mt-0.5 flex-shrink-0" />
                  )}
                  <span className="text-muted-foreground">
                    {language === 'es' && policy.risk_explanation_es
                      ? policy.risk_explanation_es
                      : policy.risk_explanation || (
                        policy.risk_score >= 70 
                          ? 'Immediate action recommended. This policy change may significantly impact vulnerable communities.'
                          : policy.risk_score >= 40
                            ? 'Monitor this policy closely. Moderate potential impact on environmental justice communities.'
                            : 'This policy appears beneficial or low-impact for environmental justice communities.'
                      )
                    }
                  </span>
                </div>
              </div>
              
              {/* Risk Factors */}
              {policy.risk_factors && policy.risk_factors.length > 0 && (
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-sm font-medium mb-2">
                    {language === 'es' ? 'Factores de Riesgo:' : 'Risk Factors:'}
                  </p>
                  <ul className="space-y-1">
                    {policy.risk_factors.map((factor, index) => (
                      <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                        <span className="text-primary mt-1">•</span>
                        {factor}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Details Card */}
          <Card data-testid="details-card">
            <CardHeader>
              <CardTitle>
                {language === 'es' ? 'Detalles' : 'Details'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">
                    {language === 'es' ? 'Jurisdicción' : 'Jurisdiction'}
                  </p>
                  <p className="font-medium">
                    {getJurisdictionLabel(policy.jurisdiction)}
                    {policy.state && ` - ${policy.state}`}
                  </p>
                </div>
              </div>
              
              <Separator />
              
              <div className="flex items-center gap-3">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">
                    {language === 'es' ? 'Fecha Efectiva' : 'Effective Date'}
                  </p>
                  <p className="font-medium">
                    {policy.effective_date || (language === 'es' ? 'Por determinar' : 'TBD')}
                  </p>
                </div>
              </div>
              
              <Separator />
              
              <div className="flex items-center gap-3">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">
                    {language === 'es' ? 'Categoría' : 'Category'}
                  </p>
                  <p className="font-medium">{getCategoryLabel(policy.category)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PolicyDetail;
